import { endOfYear, lastDayOfMonth } from 'date-fns';
import * as Sentry from '@sentry/react';
import { Temporal } from 'temporal-polyfill';
import { ICustomDateFormatConfig, IFormatterDataModel } from '../../data-models/formatter.data-model';
import { KPIRequestFrequency, periodToFrequency } from '../../data-models/kpi-requests.data-model';
import { extractISODate, TemporalPlainDateFromJsDate } from '../date-utilts';
import { FMT } from '../formatter-service';
import { KpiPeriod } from '../../data-models/company-financials.data-model';
import { validateDateInput } from '../static-formatters';

export const DateFormattersId = {
  YYYY: 'YYYY',
  YY: 'YY',
  'MMM yyyy': 'monthYear',
  date: 'date',
  dateLocal: 'dateLocal',
  dateNumeric: 'dateNumeric',
  dateShort: 'dateShort',
  dateTime: 'dateTime',
  dateTimeShort: 'dateTimeShort',
  dayMonth: 'dayMonth',
  monthLong: 'monthLong',
  monthYear: 'monthYear',
  monthYearLong: 'monthYearLong',
  time: 'time', // hh:mm:ss
  time12hr: 'time12hr',
  timeLocal: 'timeLocal',
} as const;
export type DateFormattersId = (typeof DateFormattersId)[keyof typeof DateFormattersId];

export const DateFormattersConfig: Record<DateFormattersId, IFormatterDataModel<ICustomDateFormatConfig>> = {
  YYYY: {
    config: { timeZone: 'UTC', year: 'numeric' },
    id: 'YYYY',
    type: 'date',
  },
  YY: {
    config: { timeZone: 'UTC', year: '2-digit' },
    id: 'yy',
    type: 'date',
  },
  date: {
    config: {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    },
    id: 'date',
    type: 'date',
  },
  dateLocal: {
    config: {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    },
    id: 'dateLocal',
    type: 'date',
  },
  dateNumeric: {
    config: { timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' },
    id: 'dateNumeric',
    type: 'date',
  },
  dateShort: {
    config: {
      timeZone: 'UTC',
      month: 'short',
      day: '2-digit',
    },
    id: 'dateShort',
    type: 'date',
  },
  dateTime: {
    config: {
      // if time is included, we probably want local tz
      dateStyle: 'medium',
      timeStyle: 'medium',
    },
    id: 'dateTime',
    type: 'date',
  },
  dateTimeShort: {
    // Local time, minutes only: an upload timestamp reads "Sep 04, 2026, 10:30 AM".
    config: {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
    id: 'dateTimeShort',
    type: 'date',
  },
  dayMonth: {
    config: {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short',
    },
    id: 'dayMonth',
    type: 'date',
  },
  monthLong: {
    config: {
      timeZone: 'UTC',
      month: 'long',
    },
    id: 'monthLong',
    type: 'date',
  },
  monthYear: {
    config: { timeZone: 'UTC', month: 'short', year: 'numeric' },
    id: 'monthYear',
    type: 'date',
  },
  monthYearLong: {
    config: {
      timeZone: 'UTC',
      month: 'long',
      year: 'numeric',
    },
    id: 'monthYearLong',
    type: 'date',
  },
  time: {
    config: {
      timeZone: 'UTC',
      timeStyle: 'medium',
      hour12: false,
    },
    id: 'time',
    type: 'date',
  },
  time12hr: {
    config: {
      timeZone: 'UTC',
      timeStyle: 'medium',
      hour12: true,
    },
    id: 'time12hr',
    type: 'date',
  },
  timeLocal: {
    config: {
      timeStyle: 'medium',
      hour12: true,
    },
    id: 'timeLocal',
    type: 'date',
  },
};

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatMonthLong(date: string | Date) {
  return FMT.format('monthLong', date);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatISODateOnly(date: string | Date) {
  if (typeof date === 'string') {
    return extractISODate(date);
  }
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatDateYYYY(dateAsISOString: string) {
  return FMT.format('YYYY', dateAsISOString);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatDateNumeric(dateAsISOString: string | Date) {
  return FMT.format('dateNumeric', dateAsISOString);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatDate(dateAsISOString?: string | null) {
  return FMT.format('date', dateAsISOString);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatDateShort(dateAsISOString: string | Date) {
  return FMT.format('dateShort', dateAsISOString);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatMonthYear(date: string | Date) {
  return FMT.format('monthYear', date);
}

/** @deprecated use {@link FormatterService.format} directly instead. */
export function formatMonthYearLong(date: string) {
  return FMT.format('monthYearLong', date);
}

export const getDaysBetween = (date1String: string, date2String: string) => {
  const d1: Date = new Date(date1String);
  const d2: Date = new Date(date2String);
  return Math.round((d1.getTime() - d2.getTime()) / (1000 * 3600 * 24));
};

/**
 * wrapper for {@link fiscalDateFormatter} that converts the period to a frequency and uses the current year for the fye
 * @param period: "Month" | "Quarter" | "Year"
 * @param periodISODate: timeless ISO date string of the period
 * @param fye: fiscal year end month (1-12)
 */
export function fiscalDateFormatter2({
  period,
  periodISODate,
  fye = 12,
}: {
  period: KpiPeriod;
  periodISODate: string;
  fye?: number;
}) {
  const frequency = periodToFrequency[period];
  const fyeDateString = lastDayOfMonth(new Date(new Date().getFullYear(), fye - 1, 1)).toISOString();

  return fiscalDateFormatter(frequency, periodISODate, fyeDateString);
}

export function fiscalDateFormatter(
  frequency: KPIRequestFrequency,
  periodISODate: string,
  fyeISODate?: string
): string {
  const periodValidationRes = validateDateInput(periodISODate);
  const fyeValidationRes = validateDateInput(fyeISODate);
  if (!periodValidationRes.valid || (!fyeValidationRes.valid && fyeValidationRes.reason !== 'null')) {
    Sentry.logger.error('Invalid date input', { periodISODate, fyeISODate });
    return 'Invalid Date';
  }

  switch (frequency) {
    case KPIRequestFrequency.Monthly:
      return FMT.format('monthYear', periodISODate);

    case KPIRequestFrequency.Quarterly: {
      const fyeDate = fyeISODate ? new Date(fyeISODate) : endOfYear(new Date());
      const period = new Date(periodISODate);
      const fyeMonth = fyeDate.getMonth() + 1;
      const { fiscalQuarter, plainDate } = getFiscalQuarter(period, fyeMonth);

      if (fyeMonth === 12) {
        return `Q${fiscalQuarter} ${period.getFullYear()}`;
      }

      const month2Digit = plainDate.month.toString().padStart(2, '0');
      return `Quarter ending ${month2Digit}/${plainDate.year}`;
    }

    case KPIRequestFrequency.Annual: {
      const fyeDate = fyeISODate ? new Date(fyeISODate) : endOfYear(new Date());
      const period = new Date(periodISODate);
      const fyeMonth = fyeDate.getMonth() + 1;

      const { fiscalYear } = getFiscalYear(period, fyeMonth);
      if (fyeMonth === 12) {
        return fiscalYear.toString();
      }

      return `${fiscalYear - 1}-${fiscalYear.toString().substring(2, 4)}`;
    }

    case KPIRequestFrequency.None:
    default:
      return '';
  }
}

/**
 * Returns the fiscal quarter and the end date of the quarter the given date falls in.
 */
export function getFiscalQuarter(
  date: Date | Temporal.PlainDate,
  fyeIsoMonth = 12
): { fiscalQuarter: number; plainDate: Temporal.PlainDate } {
  const plainDate = date instanceof Date ? TemporalPlainDateFromJsDate(date) : date;
  const dateCalendarMonth = plainDate.month;
  const fyeOffset = 12 - fyeIsoMonth;
  const monthMappedToBaseRange = ((dateCalendarMonth - 1 + fyeOffset) % 12) + 1;

  const fiscalQuarter = Math.ceil(monthMappedToBaseRange / 3);
  const dateOffSetToQuarterEnd = fiscalQuarter * 3 - monthMappedToBaseRange;

  const dateT = Temporal.PlainDate.from({
    year: plainDate.year,
    month: plainDate.month,
    day: 15,
  });
  const quarterEndDate = dateT.add({ months: dateOffSetToQuarterEnd }).with({ day: 32 });

  return { fiscalQuarter, plainDate: quarterEndDate };
}

export function getFiscalYear(
  date: Date,
  fyeIsoMonth = 12
): { fiscalYear: number; plainDate: Temporal.PlainDate } {
  const givenCalendarMonth = date.getMonth() + 1;
  const givenYear = date.getFullYear();

  if (givenCalendarMonth > fyeIsoMonth) {
    return {
      fiscalYear: givenYear + 1,
      plainDate: Temporal.PlainDate.from({
        year: givenYear + 1,
        month: fyeIsoMonth,
        day: 32,
      }),
    };
  } else {
    return {
      fiscalYear: givenYear,
      plainDate: Temporal.PlainDate.from({ year: givenYear, month: fyeIsoMonth, day: 32 }),
    };
  }
}
